import React from 'react';

/**
 * BlobPreloader
 *
 * Inspired by the "Blob preloader animation" Dribbble concept.
 * Uses an SVG goo / metaball filter so multiple coloured circles
 * merge and split as they orbit, producing the trademark organic
 * morphing effect (much smoother than a CSS spinner).
 *
 * Props:
 *  - size: visual square size in px (default 96)
 *  - colors: optional [c1, c2, c3] override (defaults to teal palette)
 *  - label: optional caption rendered beneath the blob
 */
export const BlobPreloader = ({ size = 96, colors, label }) => {
  const palette = colors || ['#2eb4ac', '#5fd6cf', '#1d8780'];
  // Unique id so multiple instances on a page don't share filter refs
  const uid = React.useId().replace(/:/g, '');

  return (
    <div
      className="inline-flex flex-col items-center justify-center gap-3"
      data-testid="blob-preloader"
      role="status"
      aria-label={label || 'Loading'}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <filter id={`goo-${uid}`}>
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -11"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>

        <g filter={`url(#goo-${uid})`}>
          {/* Orbiting blob 1 */}
          <g style={{ transformOrigin: '50px 50px', animation: 'blob-orbit-1 2.4s cubic-bezier(.4,0,.2,1) infinite' }}>
            <circle cx="50" cy="20" r="11" fill={palette[0]}>
              <animate attributeName="r" values="11;13;9;11" dur="2.4s" repeatCount="indefinite" />
            </circle>
          </g>
          {/* Orbiting blob 2 */}
          <g style={{ transformOrigin: '50px 50px', animation: 'blob-orbit-2 2.4s cubic-bezier(.4,0,.2,1) infinite' }}>
            <circle cx="50" cy="20" r="9" fill={palette[1]}>
              <animate attributeName="r" values="9;12;10;9" dur="2.4s" repeatCount="indefinite" />
            </circle>
          </g>
          {/* Orbiting blob 3 */}
          <g style={{ transformOrigin: '50px 50px', animation: 'blob-orbit-3 2.4s cubic-bezier(.4,0,.2,1) infinite' }}>
            <circle cx="50" cy="20" r="10" fill={palette[2]}>
              <animate attributeName="r" values="10;8;13;10" dur="2.4s" repeatCount="indefinite" />
            </circle>
          </g>
          {/* Static centre blob — the anchor the others bleed into */}
          <circle cx="50" cy="50" r="12" fill={palette[0]}>
            <animate attributeName="r" values="12;14;11;12" dur="2.4s" repeatCount="indefinite" />
          </circle>
        </g>
      </svg>

      {label && (
        <span className="text-xs font-medium text-slate-500 tracking-wide animate-pulse">
          {label}
        </span>
      )}

      {/* Keyframes injected inline so the component is self-contained */}
      <style>{`
        @keyframes blob-orbit-1 {
          0%   { transform: rotate(0deg) translateY(0); }
          50%  { transform: rotate(180deg) translateY(-6px); }
          100% { transform: rotate(360deg) translateY(0); }
        }
        @keyframes blob-orbit-2 {
          0%   { transform: rotate(120deg) translateY(0); }
          50%  { transform: rotate(300deg) translateY(-6px); }
          100% { transform: rotate(480deg) translateY(0); }
        }
        @keyframes blob-orbit-3 {
          0%   { transform: rotate(240deg) translateY(0); }
          50%  { transform: rotate(60deg)  translateY(-6px); }
          100% { transform: rotate(600deg) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default BlobPreloader;
